import React from 'react';
import { Download, Share2, Calendar, Users } from 'lucide-react';
import EGAPRadarChart from './RadarChart';

const Results: React.FC = () => {
  const mockData = {
    empathy: 75,
    growth: 82,
    adaptability: 68,
    purpose: 90
  };

  const overallScore = Math.round((mockData.empathy + mockData.growth + mockData.adaptability + mockData.purpose) / 4);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assessment Results</h1>
            <p className="text-gray-600 mt-1">
              Complete analysis of your team's cultural strengths and opportunities
            </p>
          </div>
          <div className="flex space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="h-4 w-4" />
              <span>Export Report</span>
            </button>
          </div>
        </div>

        {/* Overall Score Card */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">{overallScore}</div>
              <div className="text-lg font-semibold text-gray-900">Overall EGAP Score</div>
              <div className="text-sm text-gray-500">Out of 100</div>
            </div>
            <div className="flex items-center justify-center">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-2xl">🎯</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Assessment Date:</span>
                <span className="text-sm font-medium">July 29, 2025</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Participants:</span>
                <span className="text-sm font-medium">142 team members</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Response Rate:</span>
                <span className="text-sm font-medium">87%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Radar Chart */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">EGAP Profile</h2>
            <EGAPRadarChart data={mockData} />
          </div>

          {/* Detailed Scores */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Detailed Breakdown</h2>
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">Empathy</span>
                  <span className="text-lg font-bold text-empathy">{mockData.empathy}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-empathy h-3 rounded-full" style={{ width: `${mockData.empathy}%` }}></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">Strong emotional intelligence and understanding</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">Growth Mindset</span>
                  <span className="text-lg font-bold text-growth">{mockData.growth}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-growth h-3 rounded-full" style={{ width: `${mockData.growth}%` }}></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">Excellent learning orientation and development focus</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">Adaptability</span>
                  <span className="text-lg font-bold text-adaptability">{mockData.adaptability}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-adaptability h-3 rounded-full" style={{ width: `${mockData.adaptability}%` }}></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">Room for improvement in change management</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">Purpose</span>
                  <span className="text-lg font-bold text-purpose">{mockData.purpose}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-purpose h-3 rounded-full" style={{ width: `${mockData.purpose}%` }}></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">Outstanding sense of mission and meaning</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Key Recommendations</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-900 mb-2">🎯 Priority Focus: Adaptability</h3>
              <p className="text-yellow-800 text-sm mb-3">
                Your lowest scoring area. Consider implementing change management training and agile practices.
              </p>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Introduce regular retrospectives</li>
                <li>• Provide change management workshops</li>
                <li>• Create cross-functional projects</li>
              </ul>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-900 mb-2">💪 Leverage Strength: Purpose</h3>
              <p className="text-green-800 text-sm mb-3">
                Exceptional purpose alignment. Use this to drive other improvements.
              </p>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Share success stories more widely</li>
                <li>• Use purpose to motivate change</li>
                <li>• Mentor other teams on purpose clarity</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Items */}
        <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Suggested Next Steps</h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Schedule Follow-up Assessment</h3>
                <p className="text-sm text-gray-600">Plan next assessment in 3-6 months to track progress</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Users className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Conduct Team Interviews</h3>
                <p className="text-sm text-gray-600">Deep dive with key team members to understand context</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Download className="h-5 w-5 text-purple-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Create Action Plan</h3>
                <p className="text-sm text-gray-600">Develop specific initiatives to address identified gaps</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;